import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../services/api';
import {
  isHardArchiveStorageClass,
  isSoftArchiveStorageClass,
} from '../constants/storageClasses';

/**
 * Shape of an S3 object used by the file browser. Kept in sync with FileBrowser.tsx.
 */
export interface S3Object {
  key: string;
  size: number;
  lastModified: string;
  isImage: boolean;
  isVideo: boolean;
  isFolder: boolean;
  name: string;
  storageClass?: string;
}

interface FileItemProps {
  object: S3Object;
  onDownload: () => void;
  onPreview: () => void;
  onDelete: () => void;
  canDownload: boolean;
  /** Preview is its own permission — it no longer implies download. */
  canPreview: boolean;
  canDelete: boolean;
  bucketName: string;
  formatFileSize: (bytes: number) => string;
  viewMode: 'list' | 'grid';
  isSelected?: boolean;
  onToggleSelect?: () => void;
  /** Called once the thumbnail signed URL has been fetched, so the parent can
   *  pre-populate its preview URL cache and avoid a second Lambda call on preview. */
  onThumbnailLoaded?: (key: string, url: string) => void;
}

/**
 * Renders a single file entry in the FileBrowser in either list or grid view.
 * Handles thumbnail loading, selection checkbox, and per-file action buttons.
 */
const FileItem: React.FC<FileItemProps> = ({
  object,
  onDownload,
  onPreview,
  onDelete,
  canDownload,
  canPreview,
  canDelete,
  bucketName,
  formatFileSize,
  viewMode,
  isSelected = false,
  onToggleSelect,
  onThumbnailLoaded,
}) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  // Derived archive-tier flags — computed once, used by both views
  const isHardArchive = isHardArchiveStorageClass(object.storageClass);
  const isSoftArchive = isSoftArchiveStorageClass(object.storageClass);

  const getThumbnail = useCallback(async () => {
    try {
      const response = await apiCall(`/s3/thumbnail/${bucketName}/${object.key.split('/').map(s => encodeURIComponent(s)).join('/')}?preview=false`);
      setThumbnailUrl(response.thumbnailUrl);
      // Notify parent so it can pre-populate the preview URL cache.
      // The signed URL is the same CloudFront path regardless of ?preview= flag —
      // reusing it avoids a second Lambda call and a second browser fetch on preview.
      onThumbnailLoaded?.(object.key, response.thumbnailUrl as string);
    } catch (error) {
      console.error('Error getting thumbnail:', error);
    }
  }, [bucketName, object.key, onThumbnailLoaded]);

  useEffect(() => {
    // Hard-Archive (GLACIER / DEEP_ARCHIVE) objects must never trigger a thumbnail
    // fetch — those objects are inaccessible via GetObject and the API will return 403.
    // GIR (GLACIER_IR) objects are allowed through.
    // Thumbnails are a form of preview, so they follow the preview permission
    // rather than download. The backend enforces the same split.
    if (canPreview && (object.isImage || object.isVideo) && !isHardArchive) {
      getThumbnail();
    }
  }, [object.key, object.isImage, object.isVideo, canPreview, isHardArchive, getThumbnail]);

  // Preview is gated on the preview permission, media type, AND not being Hard-Archive
  const canPreviewMedia = canPreview && (object.isImage || object.isVideo) && !isHardArchive;

  // ---------------------------------------------------------------------------
  // Archive badge — rendered adjacent to the filename in both views
  // ---------------------------------------------------------------------------
  const archiveBadge = isHardArchive ? (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800"
      title="Glacier archive (GFR/GDA) — no operations permitted"
    >
      Archived
    </span>
  ) : isSoftArchive ? (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700"
      title="Glacier Instant Retrieval — download only"
    >
      Instant Archive
    </span>
  ) : null;

  const getFileIcon = () => {
    if (object.isImage) {
      return (
        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    } else if (object.isVideo) {
      return (
        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      );
    }
    return (
      <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  };

  if (viewMode === 'grid') {
    return (
      <div className={`group bg-white border-2 rounded-xl p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200 ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
        <div className="flex flex-col items-center text-center">
          {/* Checkbox — always enabled (selection must remain possible for freeze banner) */}
          {onToggleSelect && (
            <div className="absolute top-2 left-2">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => { e.stopPropagation(); onToggleSelect(); }}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
              />
            </div>
          )}
          <div className="w-16 h-16 mb-3 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
            {thumbnailUrl && canPreview ? (
              <div className="relative cursor-pointer w-full h-full" onClick={canPreviewMedia ? onPreview : undefined}>
                {object.isVideo ? (
                  <div className="relative w-full h-full">
                    <video src={thumbnailUrl} className="w-full h-full object-cover" muted preload="metadata" />
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                ) : (
                  <img src={thumbnailUrl} alt={object.name} className="w-full h-full object-cover" />
                )}
              </div>
            ) : (
              <div className={`w-full h-full flex items-center justify-center ${canPreviewMedia ? 'cursor-pointer' : ''}`} onClick={canPreviewMedia ? onPreview : undefined}>
                {getFileIcon()}
              </div>
            )}
          </div>
          <p className="text-sm font-medium text-gray-900 truncate w-full mb-1" title={object.name}>{object.name}</p>
          {/* Archive badge adjacent to filename */}
          {archiveBadge && <div className="mb-1">{archiveBadge}</div>}
          <p className="text-xs text-gray-500 mb-3">{formatFileSize(object.size)}</p>
          <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
            {/* Preview — suppressed entirely for Hard-Archive */}
            {canPreviewMedia && (
              <button onClick={onPreview} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors">Preview</button>
            )}
            {/* Download — disabled (not hidden) for Hard-Archive */}
            {canDownload && (
              <button
                onClick={isHardArchive ? undefined : onDownload}
                disabled={isHardArchive}
                title={isHardArchive ? 'Cannot download: file is in Glacier archive storage (GFR/GDA)' : undefined}
                className={`text-xs px-2 py-1 rounded transition-colors ${isHardArchive ? 'bg-green-100 text-green-700 opacity-50 cursor-not-allowed' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
              >
                Download
              </button>
            )}
            {/* Delete — disabled (not hidden) for Hard-Archive */}
            {canDelete && (
              <button
                onClick={isHardArchive ? undefined : onDelete}
                disabled={isHardArchive}
                title={isHardArchive ? 'Cannot delete: file is in Glacier archive storage (GFR/GDA)' : undefined}
                className={`text-xs px-2 py-1 rounded transition-colors ${isHardArchive ? 'bg-red-100 text-red-700 opacity-50 cursor-not-allowed' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // List view
  // ---------------------------------------------------------------------------
  return (
    <div className={`flex items-center justify-between p-4 border rounded-xl hover:bg-gray-50 hover:border-blue-300 transition-all duration-200 group ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
      <div className="flex items-center space-x-4">
        {/* Checkbox — always enabled (selection must remain possible for freeze banner) */}
        {onToggleSelect && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => { e.stopPropagation(); onToggleSelect(); }}
            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer flex-shrink-0"
          />
        )}
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
          {thumbnailUrl && canPreview ? (
            <div className="relative cursor-pointer w-full h-full" onClick={canPreviewMedia ? onPreview : undefined}>
              {object.isVideo ? (
                <div className="relative w-full h-full">
                  <video src={thumbnailUrl} className="w-full h-full object-cover" muted preload="metadata" />
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              ) : (
                <img src={thumbnailUrl} alt={object.name} className="w-full h-full object-cover" />
              )}
            </div>
          ) : (
            <div className={`w-full h-full flex items-center justify-center ${canPreviewMedia ? 'cursor-pointer' : ''}`} onClick={canPreviewMedia ? onPreview : undefined}>
              {getFileIcon()}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-gray-900 truncate">{object.name}</p>
            {/* Archive badge adjacent to filename */}
            {archiveBadge}
          </div>
          <p className="text-xs text-gray-500">
            {formatFileSize(object.size)} • {new Date(object.lastModified).toLocaleDateString()}
          </p>
        </div>
      </div>
      <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
        {/* Preview — suppressed entirely for Hard-Archive */}
        {canPreviewMedia && (
          <button onClick={onPreview} className="text-blue-600 hover:text-blue-800 text-sm px-3 py-1 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors">Preview</button>
        )}
        {/* Download — disabled (not hidden) for Hard-Archive */}
        {canDownload && (
          <button
            onClick={isHardArchive ? undefined : onDownload}
            disabled={isHardArchive}
            title={isHardArchive ? 'Cannot download: file is in Glacier archive storage (GFR/GDA)' : undefined}
            className={`text-sm px-3 py-1 border rounded-lg transition-colors ${isHardArchive ? 'text-green-600 border-green-600 opacity-50 cursor-not-allowed' : 'text-green-600 hover:text-green-800 border-green-600 hover:bg-green-50'}`}
          >
            Download
          </button>
        )}
        {/* Delete — disabled (not hidden) for Hard-Archive */}
        {canDelete && (
          <button
            onClick={isHardArchive ? undefined : onDelete}
            disabled={isHardArchive}
            title={isHardArchive ? 'Cannot delete: file is in Glacier archive storage (GFR/GDA)' : undefined}
            className={`text-sm px-3 py-1 border rounded-lg transition-colors ${isHardArchive ? 'text-red-600 border-red-600 opacity-50 cursor-not-allowed' : 'text-red-600 hover:text-red-800 border-red-600 hover:bg-red-50'}`}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
};

export default FileItem;
